import { Column } from "@tanstack/react-table";
import { Input } from "components/ui/input";

function facetedOptions<T>(column: Column<T, unknown>) {
  const values = Array.from(
    column.getFacetedUniqueValues().keys(),
  ) as unknown[];
  return values
    .filter((v): v is string => typeof v === "string" && v !== "")
    .sort()
    .map((v) => ({ label: v, value: v }));
}

// Renders a text or select filter input for a column, based on its `meta.filterVariant`.
// "select" options default to the distinct values actually present in the loaded data.
export function ColumnFilterInput<T>({
  column,
}: {
  column: Column<T, unknown>;
}) {
  const meta = column.columnDef.meta;
  const filterValue = (column.getFilterValue() as string) ?? "";

  if (meta?.filterVariant === "select") {
    const options = meta.filterOptions ?? facetedOptions(column);
    return (
      <select
        aria-label={`Filter ${column.id}`}
        className="mt-1 min-w-22 border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-xs text-slate-700 outline-none focus:border-blue-500"
        value={filterValue}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => column.setFilterValue(e.target.value || undefined)}
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <Input
      aria-label={`Filter ${column.id}`}
      className="mt-1 h-7 w-full min-w-24 rounded-none border-x-0 border-t-0 px-0 text-xs shadow-none focus-visible:ring-0"
      placeholder="Filter…"
      value={filterValue}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => column.setFilterValue(e.target.value || undefined)}
    />
  );
}
